import escapeTextContentForBrowser from "escape-html";
import z from "zod";

import avatarMissing from "@/assets/images/placeholders/avatar-missing.png";
import headerMissing from "@/assets/images/placeholders/header-missing.png";
import { unescapeHTML } from "@/utils/html";

import { customEmojiSchema } from "./custom-emoji";
import { relationshipSchema } from "./relationship";
import {
  contentSchema,
  dateSchema,
  filteredArray,
  stringToBooleanSchema,
} from "./utils";

import type { Resolve } from "@/utils/types";

const featuresSchema = z.object({
  boosts: z.boolean().catch(false),
  edits_enabled: z.boolean().catch(false),
  edits_visible: z.boolean().catch(false),
  for_you: z.boolean().catch(false),
  gems_enabled: z.boolean().catch(false),
  gems_visible: z.boolean().catch(false),
  predictions: z.boolean().catch(false),
  schedule_enabled: z.boolean().catch(false),
  schedule_visible: z.boolean().catch(false),
  sports: z.boolean().catch(false),
  tv: z.boolean().catch(false),
  watch_tab: z.boolean().catch(false),
});

const baseAccountSchema = z.object({
  acct: z.string().catch(""),
  accepting_messages: z.boolean().catch(false),
  avatar: z.string().catch(avatarMissing),
  avatar_static: z.string().url().optional().catch(undefined),
  bookmarks_onboarded: z.boolean().optional().catch(true),
  bot: z.boolean().catch(false),
  cdna: z
    .object({
      member_id: z.string().nullable().catch(null),
      onboarded_status: z
        .enum(["none", "active", "failed"])
        .nullable()
        .catch("none"),
    })
    .optional(),
  created_at: dateSchema,
  discoverable: z.boolean().catch(false),
  display_name: z.string().catch(""),
  emojis: filteredArray(customEmojiSchema),
  external_account_id: z.string().nullable().catch(null),
  features: featuresSchema.optional(),
  feeds_onboarded: z.boolean().optional().catch(true),
  followers_count: z.number().catch(0),
  following_count: z.number().catch(0),
  group_reactions_onboarded: z.boolean().optional().catch(true),
  header: z.string().url().catch(headerMissing),
  header_static: z.string().url().optional().catch(undefined),
  id: z.string(),
  last_status_at: dateSchema.optional().catch(undefined),
  location: z.string().optional().catch(undefined),
  locked: z.boolean().catch(false),
  moved: z.literal(null).catch(null),
  mute_expires_at: dateSchema.nullable().catch(null),
  note: contentSchema,
  /** Fedibird extra settings. */
  other_settings: z
    .object({
      location: z.string().optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
  pleroma: z
    .object({
      accepts_chat_messages: z.boolean().catch(false),
      accepts_email_list: z.boolean().catch(false),
      also_known_as: z.array(z.string().url()).catch([]),
      ap_id: z.string().url().optional().catch(undefined),
      deactivated: z.boolean().catch(false),
      favicon: z.string().url().optional().catch(undefined),
      is_moderator: z.boolean().catch(false),
      is_suggested: z.boolean().catch(false),
      location: z.string().optional().catch(undefined),
      notification_settings: z
        .object({
          block_from_strangers: z.boolean().catch(false),
        })
        .optional()
        .catch(undefined),
      relationship: relationshipSchema.optional().catch(undefined),
      settings_store: z
        .object({
          soapbox_fe: z
            .object({
              autoPlayGif: stringToBooleanSchema.catch(true),
              autoPlayVideo: stringToBooleanSchema.catch(false),
              displayMedia: z
                .enum(["default", "hide_all", "show_all"])
                .catch("default"),
              chats: z
                .object({
                  mainWindow: z.enum(["open", "minimized"]).catch("minimized"),
                  sound: stringToBooleanSchema.catch(true),
                })
                .optional()
                .catch(undefined),
              deleteModal: stringToBooleanSchema.catch(false),
              locale: z.string().catch(navigator.language || "en"),
              themeMode: z.enum(["dark", "light", "system"]).catch("system"),
              notifications: z
                .object({
                  quickFilter: z
                    .object({
                      active: z.string().optional(),
                    })
                    .optional()
                    .catch(undefined),
                })
                .optional()
                .catch(undefined),
              home: z
                .object({
                  shows: z
                    .object({
                      reblog: stringToBooleanSchema.catch(true),
                      reply: stringToBooleanSchema.catch(true),
                    })
                    .optional()
                    .catch(undefined),
                })
                .optional()
                .catch(undefined),
              videoPlayer: z
                .object({
                  volume: z.number().catch(50),
                  muted: stringToBooleanSchema.catch(false),
                })
                .optional()
                .catch(undefined),
            })
            .optional()
            .catch(undefined),
        })
        .optional()
        .catch(undefined),
      tags: z.array(z.string()).catch([]),
    })
    .optional()
    .catch(undefined),
  ppid: z.string().optional().catch(undefined),
  premium: z.boolean().catch(false),
  source: z
    .object({
      accepted_status_edit_prompt: z.boolean().catch(true),
      accepting_messages: z.boolean().catch(true),
      approved: z.boolean().catch(true),
      bookmarks_onboarded: z.boolean().optional().catch(true),
      chats_onboarded: z.boolean().optional().catch(true),
      email: z.string().catch(""),
      email_verified: z.boolean().catch(true),
      everyone_notifications: z.boolean().catch(true),
      feeds_onboarded: z.boolean().optional().catch(true),
      fields: z.array(z.string()).catch([]),
      gems_count: z.number().catch(0),
      group_reactions_onboarded: z.boolean().optional().catch(true),
      note: z.string().catch(""),
      pleroma: z
        .object({
          discoverable: z.boolean().catch(true),
        })
        .optional()
        .catch(undefined),
      sms: z.boolean().optional().catch(false),
      sms_country: z.string().nullable().catch(null),
      sms_reverification_required: z.boolean().optional().catch(false),
      sms_last_four_digits: z.string().optional().catch(undefined),
      sms_reverification_days_left: z.number().optional().catch(undefined),
      sms_verified: z.boolean().catch(false),
      tv_onboarded: z.boolean().optional().catch(true),
      unauth_visibility: z.boolean().catch(false),
      receive_marketing_message_alerts: z.boolean().optional().catch(true),
      max_status_characters: z.number().optional().catch(undefined),
    })
    .optional(),
  show_nonmember_group_statuses: z.boolean().catch(false),
  statuses_count: z.number().catch(0),
  suspended: z.boolean().catch(false),
  tv_account: z.boolean().optional().catch(false),
  tv_onboarded: z.boolean().optional().catch(true),
  tv_token: z.string().optional().catch(undefined),
  uri: z.string().url().catch(""),
  url: z.string().url().catch(""),
  username: z.string().catch(""),
  verified: z.boolean().catch(false),
  website: z.string().catch(""),
});

type BaseAccount = z.infer<typeof baseAccountSchema>;
type TransformableAccount = Omit<BaseAccount, "moved">;

const getDomain = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
};

/** Add internal fields to the account. */
const transformAccount = <T extends TransformableAccount>({
  pleroma,
  other_settings,
  ...account
}: T) => {
  const username = account.username || account.acct;
  const displayName =
    account.display_name.trim().length === 0 ? username : account.display_name;
  const domain = getDomain(account.url || account.uri);

  return {
    ...account,
    avatar_static: account.avatar_static || account.avatar,
    discoverable:
      account.discoverable || account.source?.pleroma?.discoverable || false,
    display_name: displayName,
    display_name_html: escapeTextContentForBrowser(displayName),
    domain,
    header_static: account.header_static || account.header,
    moderator: pleroma?.is_moderator || false,
    location:
      account.location || pleroma?.location || other_settings?.location || "",
    note_emojified: account.note,
    note_plain: unescapeHTML(account.note ?? ""),
    pleroma: (() => {
      if (!pleroma) return undefined;
      const { relationship, ...rest } = pleroma;
      return rest;
    })(),
    relationship: pleroma?.relationship,
    settings: pleroma?.settings_store?.soapbox_fe,
    source: {
      ...account.source,
    },
    staff: pleroma?.is_moderator || false,
    suspended: account.suspended || pleroma?.deactivated || false,
    username,
  };
};

const accountSchema = baseAccountSchema
  .extend({
    moved: baseAccountSchema.transform(transformAccount).nullable().catch(null),
  })
  .transform(transformAccount);

type Account = Resolve<z.infer<typeof accountSchema>>;

export { accountSchema, avatarMissing, headerMissing, type Account };
